/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  ACTIVITY_EVENT_BASE_URL,
  APPLICATION_ACTIVITIES_EVENTS_URL,
  APPLICATION_BASE_URL,
  APPLICATION_FLAGS_URL,
  APPLICATIONS_BASE_URL,
  QUESTION_ANSWERS_URL,
  USERPROFILE_ACTIVITIES_EVENTS_URL,
} from "Constants/SagaConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import {
  IApplicationFormActivitiesEventsRequest,
  IUserprofileActivitiesEventsRequest,
} from "Interfaces/Application/IActivitiesEventsRequest";
import { IActivityEventRequest } from "Interfaces/Application/IActivityEventRequest";
import { IApplicationAssign } from "Interfaces/Application/IApplicationAssign";
import { IApplicationDataResult } from "Interfaces/Application/IApplicationDataResult";
import { IApplicationDetailsDataResult } from "Interfaces/Application/IApplicationDetailsDataResult";
import { IApplicationFlagRequest } from "Interfaces/Application/IApplicationFlagRequest";
import { IApplicationFlagResponse } from "Interfaces/Application/IApplicationFlagResponse";
import { IApplicationFormActivityEvent } from "Interfaces/Application/IApplicationFormActivityEvent";
import { IApplicationGetAssign } from "Interfaces/Application/IApplicationGetAssign";
import { IApplicationQuestionResponse } from "Interfaces/Application/IApplicationQuestionResponse";
import { IApplicationRequest } from "Interfaces/Application/IApplicationRequest";
import { IApplicationScoreRequest } from "Interfaces/Application/IApplicationScoreRequest";
import { IApplicationsRequest } from "Interfaces/Application/IApplicationsRequest";
import { IApplicationUserProfileInformation } from "Interfaces/Application/IApplicationUserProfileInformation";
import { IManageApplicationRequest } from "Interfaces/Application/IManageApplicationRequest";
import { IApplicationsDataResult } from "Interfaces/Application/ISearchApplicationsDataResult";
import { IUpdateApplicationTechnologyAreaRequest } from "Interfaces/Application/IUpdateApplicationTechnologyAreaRequest";
import { IEventStatus } from "Interfaces/Event/IEventStatus";
import { IEventUpdateStatusDataResult } from "Interfaces/Event/IEventUpdateStatusDataResult";
import { IApiResult2 } from "Interfaces/IApiResult";

import { get, post, put, IHttpResponse } from "./ApiUtils";
import { IApplicationBulkAssign } from "Interfaces/Application/IApplicationBulkAssign";
import { IApplicationGetBulkAssign } from "Interfaces/Application/IApplicationGetBulkAssign";

/**
 * Get full detail of individual application
 */
export async function getApplication(
  selectedApplicationEmail: string
): Promise<IHttpResponse<IApiResult2<IApplicationDataResult>>> {
  return await get<IApiResult2<IApplicationDataResult>>(
    APPLICATION_BASE_URL + selectedApplicationEmail,
    true
  );
}

/**
 * Get profile information by application id
 */
export async function getProfileInformationByApplicationId(
  selectedApplicationId: number,
  tenant: string
): Promise<
  IHttpResponse<IApiResult2<IApplicationDetailsDataResult<IApplicationUserProfileInformation>>>
> {
  return await get<IApiResult2<IApplicationDetailsDataResult<IApplicationUserProfileInformation>>>(
    APPLICATIONS_BASE_URL.replace("{tenant}", tenant) + "profile/" + selectedApplicationId,
    true
  );
}

/**
 * Get application flags by application id
 */
export async function getApplicationFlagsByApplicationId(
  selectedApplicationId: number
): Promise<IHttpResponse<IApiResult2<IApplicationDetailsDataResult<IApplicationFlagResponse[]>>>> {
  return await get<IApiResult2<IApplicationDetailsDataResult<IApplicationFlagResponse[]>>>(
    APPLICATION_FLAGS_URL.replace("{tenant}", "mvp") + selectedApplicationId,
    true
  );
}

/**
 * Get application questions by application id
 */
export async function getApplicationQuestionsByApplicationId(
  selectedApplicationId: number,
  tenant: string
): Promise<
  IHttpResponse<IApiResult2<IApplicationDetailsDataResult<IApplicationQuestionResponse[]>>>
> {
  return await get<IApiResult2<IApplicationDetailsDataResult<IApplicationQuestionResponse[]>>>(
    QUESTION_ANSWERS_URL.replace("{tenant}", tenant) + selectedApplicationId,
    true
  );
}

/**
 * Get assign application
 * @param applicationFormId
 */
export async function getAssignApplication(
  applicationFormId: number
): Promise<IHttpResponse<IApiResult2<IApplicationGetAssign>>> {
  return await get<IApiResult2<IApplicationGetAssign>>(
    APPLICATIONS_BASE_URL.replace("{tenant}", "mvp") + "assign/" + applicationFormId,
    true
  );
}

/**
 * Assign an application
 */
export async function assignApplication(
  assignApplicationRequest: IApplicationAssign
): Promise<IHttpResponse<IApiResult2<string[]>>> {
  return await post<IApiResult2<string[]>>(
    APPLICATIONS_BASE_URL.replace("{tenant}", "mvp") + "assign",
    assignApplicationRequest,
    true
  );
}

/**
 * Get available assignees
 * @param applicationFormId
 */
export async function getAvailableAssignees(
  applicationFormIds: number[]
): Promise<IHttpResponse<IApiResult2<IApplicationGetBulkAssign[]>>> {
  const idsParam = applicationFormIds.join(",");
  return await get<IApiResult2<IApplicationGetBulkAssign[]>>(
    APPLICATIONS_BASE_URL.replace("{tenant}", "mvp") + "available-assignees?ids=" + idsParam,
    true
  );
}

/**
 * Bulk assign applications
 */
export async function bulkAssignApplications(
  assignApplicationRequest: IApplicationBulkAssign
): Promise<IHttpResponse<IApiResult2<string[]>>> {
  return await post<IApiResult2<string[]>>(
    APPLICATIONS_BASE_URL.replace("{tenant}", "mvp") + "bulk-assign",
    assignApplicationRequest,
    true
  );
}

/**
 * Score an application
 */
export async function scoreApplication(
  scoreApplicationRequest: IApplicationScoreRequest
): Promise<IHttpResponse<IApiResult2<boolean>>> {
  return await post<IApiResult2<boolean>>(
    APPLICATIONS_BASE_URL.replace("{tenant}", "mvp") + "score",
    scoreApplicationRequest,
    true
  );
}

/**
 * Flag an application
 */
export async function flagApplication(
  flagApplicationRequest: IApplicationFlagRequest
): Promise<IHttpResponse<IApiResult2<boolean>>> {
  return await post<IApiResult2<boolean>>(
    APPLICATIONS_BASE_URL.replace("{tenant}", "mvp") + "flag",
    flagApplicationRequest,
    true
  );
}

/**
 * Manage an application
 */
export async function manageApplication(
  manageApplicationRequest: IManageApplicationRequest
): Promise<IHttpResponse<IApiResult2<string[]>>> {
  return await post<IApiResult2<string[]>>(
    APPLICATIONS_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase()) +
      "manageApplication",
    manageApplicationRequest,
    true
  );
}

/**
 * Update the technology areas of an application
 */
export async function updateApplicationTechnologyArea(
  updateApplicationTechnologyAreaRequest: IUpdateApplicationTechnologyAreaRequest
): Promise<IHttpResponse<IApiResult2<string[]>>> {
  return await post<IApiResult2<string[]>>(
    APPLICATIONS_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase()) +
      "updateTechnologyArea",
    updateApplicationTechnologyAreaRequest,
    true
  );
}

/**
 * Search all applications for the given text.
 */
export async function searchApplications(
  searchRequest: IApplicationRequest
): Promise<IHttpResponse<IApiResult2<IApplicationsDataResult>>> {
  return await post<IApiResult2<IApplicationsDataResult>>(
    APPLICATION_BASE_URL + "search",
    searchRequest,
    true
  );
}

/**
 * Search all applications for the given text.
 */
export async function getApplications(
  searchRequest: IApplicationsRequest,
  tenant: string
): Promise<IHttpResponse<IApiResult2<IApplicationsDataResult>>> {
  const url = APPLICATIONS_BASE_URL.replace("{tenant}", tenant);
  return await post<IApiResult2<IApplicationsDataResult>>(url, searchRequest, true);
}

export async function getActivitiesEventsByApplicationId(
  request: IApplicationFormActivitiesEventsRequest,
  tenant: string
): Promise<
  IHttpResponse<IApiResult2<IApplicationDetailsDataResult<IApplicationFormActivityEvent[]>>>
> {
  const url = APPLICATION_ACTIVITIES_EVENTS_URL.replace("{tenant}", tenant);
  return await post<IApiResult2<IApplicationDetailsDataResult<IApplicationFormActivityEvent[]>>>(
    url,
    request,
    true
  );
}

export async function getActivitiesEventsByUserprofileId(
  request: IUserprofileActivitiesEventsRequest,
  tenant: string
): Promise<
  IHttpResponse<IApiResult2<IApplicationDetailsDataResult<IApplicationFormActivityEvent[]>>>
> {
  const url = USERPROFILE_ACTIVITIES_EVENTS_URL.replace("{tenant}", tenant);
  return await post<IApiResult2<IApplicationDetailsDataResult<IApplicationFormActivityEvent[]>>>(
    url,
    request,
    true
  );
}
export async function getActivityEventById(
  request: IActivityEventRequest
): Promise<IHttpResponse<IApiResult2<IApplicationFormActivityEvent>>> {
  return await get<IApiResult2<IApplicationFormActivityEvent>>(
    ACTIVITY_EVENT_BASE_URL.replace("{contributionType}", request.contributionType).replace(
      "{activityEventId}",
      request.activityEventId.toString()
    ),
    true
  );
}

/**
 * Update an application's status
 */
export async function updateApplicationStatus(
  eventStatus: IEventStatus
): Promise<IHttpResponse<IApiResult2<IEventUpdateStatusDataResult>>> {
  return await put<IApiResult2<IEventUpdateStatusDataResult>>(
    APPLICATION_BASE_URL + "UpdateEventStatus/",
    eventStatus,
    true
  );
}

/**
 * Download applications.
 */
export async function downloadApplicationsToCSV(
  request: any,
  tenant
): Promise<IHttpResponse<IApiResult2<string>>> {
  const url = `${APPLICATIONS_BASE_URL.replace("{tenant}", tenant)}download`;
  return await post<IApiResult2<string>>(url, request, true, false, {
    method: "POST",
    body: JSON.stringify(request),
    headers: { "Content-Type": "application/octet-stream" },
  });
}
